<template>
  <div>
    <NdsDropdown
      :max-height="600"
      :placement="NdsPopoverPlacement.BOTTOM_END"
    >
      <template #trigger>
        <NdsButton
          :color="NdsButtonColor.TRANSPARENT"
          :size="NdsButtonSize.S"
        >
          <span class="userMenuDropdown__userName">
            {{ store.getters.name }}
          </span>
        </NdsButton>
      </template>

      <template #dropdownContent>
        <NdsMenuItem
          data-testid="btn-switch-french"
          :is-checkable="false"
          :value="store.getters.lang === 'fr'"
          @input="setLang('fr_FR')"
        >
          <template #label>
            <div class="userMenuDropdown__item">
              <flag iso="fr" />
              <span> Français </span>
            </div>
          </template>
        </NdsMenuItem>

        <NdsMenuItem
          data-testid="btn-switch-english"
          :is-checkable="false"
          :value="store.getters.lang === 'en'"
          @input="setLang('en_US')"
        >
          <template #label>
            <div class="userMenuDropdown__item">
              <flag iso="gb" />
              <span> English </span>
            </div>
          </template>
        </NdsMenuItem>

        <div class="userMenuDropdown__divider" />

        <NdsMenuItem
          v-if="!store.getters.isPublic && store.getters.isPilotOrContributor"
          data-testid="my-objects"
          :is-checkable="false"
          @input="router.push({ name: 'my_objects' })"
        >
          <template #label>
            <div class="userMenuDropdown__item">
              <AppIcon
                class="userMenuDropdown__icon"
                color="primary"
                custom_icon="user-check"
                fa_type="far"
                icon_size="1rem"
              />
              <span><translate key="2791">My objects</translate></span>
            </div>
          </template>
        </NdsMenuItem>

        <NdsMenuItem
          data-testid="edit-profile"
          :is-checkable="false"
          @input="currentProfile = {}"
        >
          <template #label>
            <div class="userMenuDropdown__item">
              <AppIcon
                class="userMenuDropdown__icon"
                color="primary"
                custom_icon="user-circle"
                fa_type="far"
                icon_size="1rem"
              />
              <span><translate key="2801">Edit profile</translate></span>
            </div>
          </template>
        </NdsMenuItem>

        <NdsMenuItem
          data-testid="change-password"
          :is-checkable="false"
          @input="currentPassword = {}"
        >
          <template #label>
            <div class="userMenuDropdown__item">
              <AppIcon
                class="userMenuDropdown__icon"
                color="primary"
                custom_icon="lock"
                fa_type="far"
                icon_size="1rem"
              />
              <span><translate key="2811">Change password</translate></span>
            </div>
          </template>
        </NdsMenuItem>

        <NdsMenuItem
          v-if="store.getters.impersonate_contributor"
          :is-checkable="false"
          @input="impersonate(false)"
        >
          <template #label>
            <div class="userMenuDropdown__item">
              <AppIcon
                class="userMenuDropdown__icon"
                color="primary"
                custom_icon="user-ninja"
                fa_type="far"
                icon_size="1rem"
              />
              <span><translate key="2821">Normal mode</translate></span>
            </div>
          </template>
        </NdsMenuItem>

        <NdsMenuItem
          v-else-if="store.getters.isPilote && !store.getters.isPublic"
          :is-checkable="false"
          @input="impersonate(true)"
        >
          <template #label>
            <div class="userMenuDropdown__item">
              <AppIcon
                class="userMenuDropdown__icon"
                color="primary"
                custom_icon="user-ninja"
                fa_type="far"
                icon_size="1rem"
              />
              <span><translate key="2831">Contributor mode</translate></span>
            </div>
          </template>
        </NdsMenuItem>

        <NdsMenuItem
          :is-checkable="false"
          @input="downloadFile('user/cgu')"
        >
          <template #label>
            <div class="userMenuDropdown__item">
              <AppIcon
                class="userMenuDropdown__icon"
                color="primary"
                custom_icon="file-alt"
                fa_type="far"
                icon_size="1rem"
              />
              <span>{{ t('shared.terms_of_service') }}</span>
            </div>
          </template>
        </NdsMenuItem>

        <NdsMenuItem
          :is-checkable="false"
          @input="logout"
        >
          <template #label>
            <div class="userMenuDropdown__item">
              <AppIcon
                class="userMenuDropdown__icon"
                color="primary"
                custom_icon="sign-out-alt"
                fa_type="far"
                icon_size="1rem"
              />
              <span>{{ t('shared.signout') }}</span>
            </div>
          </template>
        </NdsMenuItem>

        <div class="userMenuCopyright">
          <NdsText :size="NdsTextSizes.S">
            {{ t('shared.copyright') }} TENACY {{ new Date().getFullYear() }}.
            {{ t('shared.all_rights_reserved') }} Tenacy
          </NdsText>
        </div>
      </template>
    </NdsDropdown>

    <UserPasswordModal
      ref="userPassword"
      api_endpoint_post="user/password"
      :current_obj_="currentPassword"
      current_operation="edit"
      modal_id="user_password"
      @cancelled="currentPassword = null"
    />

    <UserProfileModal
      ref="userProfile"
      api_endpoint_post="user/profile"
      :current_obj_="currentProfile"
      current_operation="edit"
      do_get
      modal_id="user_profile"
      @cancelled="currentProfile = null"
      @edited="profileEdited"
    />
  </div>
</template>

<script setup lang="ts">
import Vue, { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router/composables'

import AppIcon from '@/components/ui/AppIcon.vue'
import NdsButton from '@/components/NewDesignSystem/NdsButton/NdsButton.vue'
import NdsDropdown from '@/components/NewDesignSystem/NdsDropdown/NdsDropdown.vue'
import NdsMenuItem from '@/components/NewDesignSystem/NdsMenuItem/NdsMenuItem.vue'
import UserPasswordModal from '@/contexts/users/UserPasswordModal.vue'
import UserProfileModal from '@/contexts/users/UserProfileModal.vue'
import { NdsButtonColor, NdsButtonSize } from '@/components/NewDesignSystem/NdsButton/NdsButton.interfaces'
import { NdsPopoverPlacement } from '@/components/NewDesignSystem/NdsPopover/NdsPopover.interfaces'
import { t } from '@/I18n'
import { useStore } from '@/store'
import NdsText from '@/components/NewDesignSystem/NdsText/NdsText.vue'
import { NdsTextSizes } from '@/components/NewDesignSystem/NdsText/NdsText.interfaces'

const _ = Vue.prototype._
const $toast = Vue.prototype.$toast
const $log = Vue.prototype.$log
const $language = Vue.prototype.$language
const $moment = Vue.prototype.$moment
const downloadFile = Vue.prototype.download_file

const store = useStore()
const router = useRouter()

const currentPassword = ref<Record<string, unknown> | null>(null)
const currentProfile = ref<Record<string, unknown> | null>(null)

const setLang = (lang: string) => {
  $language.current = lang
  store
    .dispatch('setLang', lang)
    .then(() => {
      $language.current = lang
      $moment.locale(lang)
    })
    .catch((err: unknown) => {
      $toast.alert(_('Language updated failed'))
      $log.error(err)
    })
}

const impersonate = (v: boolean) => {
  store
    .dispatch('impersonate', v)
    .then((res: unknown) => {
      $log.debug(res)
    })
    .catch((err: unknown) => {
      $toast.alert(_('Mode changed failed'))
      $log.error(err)
    })
}

const logout = () => {
  $log.debug('LOGOUT')
  store.commit('loading')
  store
    .dispatch('logout')
    .then(() => {
      store.commit('unloading')
      router.push('/login').then(() => {
        window.location.reload()
      })
    })
    .catch((err: unknown) => {
      $log.error('ERROR WHILE LOGOUT' + err)
      $toast.alert(_('Logout error'))
      store.commit('unloading')
      router.push('/login')
    })
}

const profileEdited = (data: { object?: Record<string, unknown> }) => {
  $log.debug(data)
  if (data.object) {
    store.dispatch('updateProfile', data.object)
  }
}

const showProfile = () => {
  if (!store.getters.mfa_enabled && store.getters.mfa_activated && !store.getters.has_saml) {
    currentProfile.value = {}
  }
}

onMounted(() => {
  showProfile()
})
</script>

<style lang="scss" scoped>
.userMenuDropdown {
  &__userName {
    font-weight: 700;
    white-space: nowrap;
  }

  &__divider {
    width: 100%;
    height: 1px;
    background-color: var(--neutral-200);
    margin: 4px 0;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__icon {
    pointer-events: none;
  }
}

.userMenuCopyright {
  cursor: default;
  text-align: right;
  padding: var(--spacing-1);
  margin-left: var(--spacing-4);

  &__link {
    color: var(--neutral-900);
    text-decoration: underline;
    text-decoration-thickness: 0.1px;
    text-underline-offset: 2px;
  }

  .ndsText {
    color: var(--neutral-900) !important;
  }
}
</style>
